import * as THREE from 'three';
import { box, weld } from '../level/VoxelFactory.js';
import { eventBus } from '../core/EventBus.js';
import { gameState } from '../core/GameState.js';
import {
  ROW, ROW_DEPTH, FIELD_HALF, COLORS, ELECTRIC,
} from '../core/Constants.js';

function createLightningMesh(x1, x2, z, material) {
  const C1 = COLORS.ELECTRIC_ZAP || 0x7dd3fc;
  const C2 = COLORS.ELECTRIC_GLOW || 0x38bdf8;
  const C3 = 0xffffff;

  const points = [];
  const segments = 22;
  const dx = (x2 - x1) / segments;

  for (let i = 0; i <= segments; i++) {
    const px = x1 + i * dx;
    const isEnd = i === 0 || i === segments;
    const py = isEnd ? 0.06 : 0.12 + Math.random() * 0.55;
    const pz = z + (isEnd ? (Math.random() - 0.5) * 0.2 : (Math.random() - 0.5) * 1.3);
    points.push({ x: px, y: py, z: pz });
  }

  const parts = [];
  for (let i = 0; i < points.length - 1; i++) {
    const pA = points[i];
    const pB = points[i + 1];

    const cx = (pA.x + pB.x) / 2;
    const cy = (pA.y + pB.y) / 2;
    const cz = (pA.z + pB.z) / 2;

    const len = Math.hypot(pB.x - pA.x, pB.y - pA.y, pB.z - pA.z);
    const color = i % 2 === 0 ? C1 : (i % 3 === 0 ? C3 : C2);

    // Thick core bolt segment
    parts.push(box(len * 0.98, 0.14, 0.14, color, cx, cy, cz));
    // Spark node
    parts.push(box(0.22, 0.22, 0.22, C3, pB.x, pB.y, pB.z));
  }

  const geo = weld(parts);
  const mesh = new THREE.Mesh(geo, material);
  return { mesh, geo };
}

export class ElectricZapManager {
  constructor(scene) {
    this.scene = scene;
    this.material = new THREE.MeshBasicMaterial({ vertexColors: true });
    this.group = new THREE.Group();
    scene.add(this.group);

    this.freightRows = [];
    this.activeZaps = []; // array of { mesh, geo, timer }
    this.spawnTimer = 0;

    this.resetTimer();
  }

  setRows(rows) {
    this.freightRows = rows.filter((r) => r.type === ROW.FREIGHT);
    this.clear();
    this.resetTimer();
  }

  resetTimer() {
    const min = ELECTRIC?.INTERVAL_MIN || 0.3;
    const max = ELECTRIC?.INTERVAL_MAX || 1.4;
    this.spawnTimer = min + Math.random() * (max - min);
  }

  spawnZap() {
    if (this.freightRows.length === 0) return;

    const rowObj = this.freightRows[Math.floor(Math.random() * this.freightRows.length)];
    const z = -rowObj.row * ROW_DEPTH;

    // Full width across the entire tracks
    const margin = 2.5;
    const x1 = -FIELD_HALF - margin;
    const x2 = FIELD_HALF + margin;

    const zap = createLightningMesh(x1, x2, z, this.material);
    zap.timer = ELECTRIC?.ZAP_DURATION || 0.14;

    this.group.add(zap.mesh);
    this.activeZaps.push(zap);

    for (let i = 0; i < 3; i++) {
      const sparkX = (Math.random() - 0.5) * FIELD_HALF * 1.8;
      eventBus.emit('zap:spark', { x: sparkX, z });
    }
    eventBus.emit('zap:sound');
  }

  update(dt) {
    if (!gameState.game.isPlaying && !gameState.game.started) {
      this.clear();
      return;
    }

    this.spawnTimer -= dt;
    if (this.spawnTimer <= 0) {
      this.spawnZap();
      this.resetTimer();
    }

    for (let i = this.activeZaps.length - 1; i >= 0; i--) {
      const z = this.activeZaps[i];
      z.timer -= dt;
      // Rapid flicker
      z.mesh.visible = Math.sin(z.timer * 80) > -0.2;

      if (z.timer <= 0) {
        z.mesh.removeFromParent();
        z.geo.dispose();
        this.activeZaps.splice(i, 1);
      }
    }
  }

  clear() {
    for (const z of this.activeZaps) {
      z.mesh.removeFromParent();
      z.geo.dispose();
    }
    this.activeZaps.length = 0;
  }

  dispose() {
    this.clear();
    this.material.dispose();
    this.group.removeFromParent();
  }
}
